import React from "react";
import "@/App.css";
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { isNativePlatform } from "./lib/platform";
import { setupAndroidLifecycle } from "./lib/native";
import { initPushNotifications } from "./lib/nativePush";
import { toast } from "sonner";
import AppShell from "./components/AppShell";
import OfflineBanner from "./components/OfflineBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminShell from "./components/AdminShell";
import DistributorShell from "./components/DistributorShell";
import AdminImpersonationBanner from "./components/AdminImpersonationBanner";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PublicDownload from "./pages/PublicDownload";
// Legal pages — lazy-loaded so they don't bloat the main landing/app bundle.
// The SEO audit flagged /terms, /privacy-policy, and /data-deletion as slow;
// putting them behind React.lazy shrinks the initial parse cost for the
// public site while these routes stay fully client-routable.
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const DataDeletion = React.lazy(() => import("./pages/DataDeletion"));
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import Guarantors from "./pages/Guarantors";
import GuarantorProfile from "./pages/GuarantorProfile";
import CustomerProfile from "./pages/CustomerProfile";
import Loans from "./pages/Loans";
import LoanDetail from "./pages/LoanDetail";
import Payments from "./pages/Payments";
import Recovery from "./pages/Recovery";
import Reports from "./pages/Reports";
import SalesmenReport from "./pages/SalesmenReport";
import RecoveryCalendar from "./pages/RecoveryCalendar";
import Settings from "./pages/Settings";
import Subscription from "./pages/Subscription";
import RiskCheck from "./pages/RiskCheck";
import WhatsApp from "./pages/WhatsApp";
import Blacklist from "./pages/Blacklist";
import Refer from "./pages/Refer";
import ShopUsers from "./pages/ShopUsers";
import ProfitReport from "./pages/ProfitReport";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminShops from "./pages/admin/AdminShops";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminDistributors from "./pages/admin/AdminDistributors";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminPaymentVerification from "./pages/admin/AdminPaymentVerification";
import AdminCommissions from "./pages/admin/AdminCommissions";
import AdminWhatsApp from "./pages/admin/AdminWhatsApp";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminActivity from "./pages/admin/AdminActivity";
import AdminDeletions from "./pages/admin/AdminDeletions";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminVerifications from "./pages/admin/AdminVerifications";
// Feb 30 2026 FINAL — Founders module retired.
// import AdminFounders from "./pages/admin/AdminFounders";
import AdminReminders from "./pages/admin/AdminReminders";
import DistributorOverview from "./pages/distributor/DistributorOverview";
import DistributorCommissions from "./pages/distributor/DistributorCommissions";
import DistributorWallet from "./pages/distributor/DistributorWallet";
import DistributorRegister from "./pages/distributor/DistributorRegister";
import DistributorRetired from "./pages/DistributorRetired";
import Activate from "./pages/Activate";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminReferralSettings from "./pages/admin/AdminReferralSettings";
import AdminSubAdmins from "./pages/admin/AdminSubAdmins";
import SubAdminDashboard from "./pages/SubAdminDashboard";
import StaffShell from "./components/StaffShell";
import { StaffDashboard, StaffVerifications } from "./pages/StaffPages";
import StaffTrialFollowups from "./pages/StaffTrialFollowups";
import StaffShopDetail from "./pages/StaffShopDetail";

const PLATFORM_STAFF_ROLES = ["sales_executive", "sales_manager", "support_executive", "verification_executive"];

function roleHome(role) {
  if (role === "platform_admin") return "/admin";
  if (role === "sub_admin") return "/subadmin";
  if (role === "distributor") return "/distributor";
  if (PLATFORM_STAFF_ROLES.includes(role)) return "/staff";
  return "/";
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  // Inside the Android (Capacitor) wrapper we never show the marketing Landing —
  // unauthenticated users go straight to login. Landing stays for the web only.
  if (!user) {
    if (isNativePlatform()) return <Navigate to="/login" replace />;
    return <Landing />;
  }
  if (user.role === "platform_admin") return <Navigate to="/admin" replace />;
  if (user.role === "distributor") return <Navigate to="/distributor" replace />;
  if (PLATFORM_STAFF_ROLES.includes(user.role)) return <Navigate to="/staff" replace />;
  return (
    <>
      <AdminImpersonationBanner />
      <AppShell><Dashboard /></AppShell>
    </>
  );
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "owner" && user.role !== "platform_admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ShopProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  // Any user with a shop_id is a shop-scoped role — accept them and let each
  // page's own permission check gate individual actions (Phase 4 RBAC).
  // Reject only platform-scoped roles (admin, staff, distributor).
  if (user.role === "platform_admin" || user.role === "distributor" ||
      PLATFORM_STAFF_ROLES.includes(user.role)) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return (
    <>
      <AdminImpersonationBanner />
      <AppShell>{children}</AppShell>
    </>
  );
}

function StaffProtected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!PLATFORM_STAFF_ROLES.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  return <StaffShell />;
}

function SubAdminRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "sub_admin" && user.role !== "platform_admin") return <Navigate to={roleHome(user.role)} replace />;
  return <SubAdminDashboard />;
}


function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to={roleHome(user.role)} replace />;
  return <AdminShell>{children}</AdminShell>;
}

function DistributorProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "distributor") return <Navigate to={roleHome(user.role)} replace />;
  return <DistributorShell>{children}</DistributorShell>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (user) return <Navigate to={roleHome(user.role)} replace />;
  return children;
}

export default function App() {
  // HashRouter is required inside Capacitor (file:// origin) — the WebView can't
  // server-route deep URLs. On the web we keep BrowserRouter for clean URLs / SEO.
  const Router = isNativePlatform() ? HashRouter : BrowserRouter;

  // One-time Android lifecycle wiring (splash, status bar, back button,
  // network status, external-link interceptor).
  React.useEffect(() => {
    let lastOnline = true;
    setupAndroidLifecycle({
      onNetworkChange: (connected) => {
        if (connected === lastOnline) return;
        lastOnline = connected;
        if (connected) toast.success("Internet wapas connect ho gaya", { id: "net" });
        else toast.error("Offline ho. Internet check karein.", { id: "net", duration: 6000 });
      },
    });
    // FCM push — relies on the user being logged in so the /devices/register call
    // carries auth. AuthProvider mounts before this effect, but the token may
    // still be loading; init defers permission prompt until then internally.
    initPushNotifications({
      onDeepLink: (path) => {
        try { window.location.hash = `#${path}`; } catch {/* ignore */}
      },
    });
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Toaster richColors position="top-right" />
          <OfflineBanner />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/distributor/register" element={<PublicOnly><DistributorRegister /></PublicOnly>} />

          {/* Phase C — 1-click legacy referral partner activation */}
          <Route path="/activate" element={<PublicOnly><Activate /></PublicOnly>} />

          {/* Customer-facing public download routes — proxy backend PDFs without exposing backend hostname */}
          <Route path="/d/:kind/:id" element={<PublicDownload />} />

          {/* Legal pages (public, SEO-friendly, no auth) — lazy-loaded */}
          <Route
            path="/privacy-policy"
            element={
              <React.Suspense fallback={<div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>}>
                <PrivacyPolicy />
              </React.Suspense>
            }
          />
          <Route
            path="/terms"
            element={
              <React.Suspense fallback={<div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>}>
                <Terms />
              </React.Suspense>
            }
          />
          <Route
            path="/data-deletion"
            element={
              <React.Suspense fallback={<div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>}>
                <DataDeletion />
              </React.Suspense>
            }
          />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminProtected><AdminOverview /></AdminProtected>} />
          <Route path="/admin/shops" element={<AdminProtected><AdminShops /></AdminProtected>} />
          {/* /admin/founders retired — redirect to admin overview */}
          <Route path="/admin/founders" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/reminders" element={<AdminProtected><AdminReminders /></AdminProtected>} />
          <Route path="/admin/distributors" element={<AdminProtected><AdminDistributors /></AdminProtected>} />
          <Route path="/admin/commissions" element={<AdminProtected><AdminCommissions /></AdminProtected>} />
          <Route path="/admin/payments" element={<AdminProtected><AdminPayments /></AdminProtected>} />
          <Route path="/admin/verify-payments" element={<AdminProtected><AdminPaymentVerification /></AdminProtected>} />
          <Route path="/admin/verifications" element={<AdminProtected><AdminVerifications /></AdminProtected>} />
          <Route path="/admin/whatsapp" element={<AdminProtected><AdminWhatsApp /></AdminProtected>} />
          <Route path="/admin/analytics" element={<AdminProtected><AdminAnalytics /></AdminProtected>} />
          <Route path="/admin/plans" element={<AdminProtected><AdminPlans /></AdminProtected>} />
          <Route path="/admin/staff" element={<AdminProtected><AdminStaff /></AdminProtected>} />

          {/* Platform Staff portal — Feb 2026 Phase 3 */}
          <Route path="/staff" element={<StaffProtected />}>
            <Route index element={<StaffDashboard />} />
            <Route path="verifications" element={<StaffVerifications />} />
            <Route path="trial-followups" element={<StaffTrialFollowups />} />
            <Route path="shops/:id" element={<StaffShopDetail />} />
          </Route>
          <Route path="/admin/activity" element={<AdminProtected><AdminActivity /></AdminProtected>} />
          <Route path="/admin/deletions" element={<AdminProtected><AdminDeletions /></AdminProtected>} />
          <Route path="/admin/referral-settings" element={<AdminProtected><AdminReferralSettings /></AdminProtected>} />
          <Route path="/admin/sub-admins" element={<AdminProtected><AdminSubAdmins /></AdminProtected>} />

          {/* Sub Admin dashboard — role-scoped */}
          <Route path="/subadmin" element={<SubAdminRoute />} />
          <Route path="/admin/notifications" element={<AdminProtected><AdminNotifications /></AdminProtected>} />
          <Route path="/admin/settings" element={<AdminProtected><AdminSettings /></AdminProtected>} />

          {/* Distributor routes */}
          <Route path="/distributor" element={<DistributorProtected><DistributorOverview /></DistributorProtected>} />
          <Route path="/distributor/commissions" element={<DistributorProtected><DistributorCommissions /></DistributorProtected>} />
          <Route path="/distributor/wallet" element={<DistributorProtected><DistributorWallet /></DistributorProtected>} />
          {/* /distributor/shops removed Feb 2026 — partners no longer onboard shops manually */}
          <Route path="/distributor/shops" element={<Navigate to="/distributor" replace />} />

          {/* Shop routes */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/landing" element={<HomeRoute />} />
          <Route path="/customers" element={<ShopProtected><Customers /></ShopProtected>} />
          <Route path="/customers/:id" element={<ShopProtected><CustomerProfile /></ShopProtected>} />
          <Route path="/guarantors" element={<ShopProtected><Guarantors /></ShopProtected>} />
          <Route path="/guarantors/:id" element={<ShopProtected><GuarantorProfile /></ShopProtected>} />
          <Route path="/loans" element={<ShopProtected><Loans /></ShopProtected>} />
          <Route path="/loans/:id" element={<ShopProtected><LoanDetail /></ShopProtected>} />
          <Route path="/payments" element={<ShopProtected><Payments /></ShopProtected>} />
          <Route path="/recovery" element={<ShopProtected><Recovery /></ShopProtected>} />
          <Route path="/risk" element={<ShopProtected><RiskCheck /></ShopProtected>} />
          <Route path="/blacklist" element={<ShopProtected><Blacklist /></ShopProtected>} />
          <Route path="/reports/profit" element={<ShopProtected><ProfitReport /></ShopProtected>} />
          <Route path="/whatsapp" element={<ShopProtected><WhatsApp /></ShopProtected>} />
          <Route path="/reports" element={<ShopProtected><Reports /></ShopProtected>} />
          <Route path="/reports/salesmen" element={<ShopProtected><SalesmenReport /></ShopProtected>} />
          <Route path="/recovery/calendar" element={<ShopProtected><RecoveryCalendar /></ShopProtected>} />
          <Route path="/settings" element={<ShopProtected><Settings /></ShopProtected>} />
          <Route path="/subscription" element={<ShopProtected><Subscription /></ShopProtected>} />
          <Route path="/refer" element={<ShopProtected><OwnerOnly><Refer /></OwnerOnly></ShopProtected>} />
          <Route path="/users" element={<ShopProtected><OwnerOnly><ShopUsers /></OwnerOnly></ShopProtected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}
